import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Image } from '@gik/ui';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { IWordpressCalendarEventType } from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { CalendarEventTypeIds, CalendarServiceCategorySlugs } from '@gik/calendar/models/CalendarEventTypes';
import { isFoodType } from '../EventForm/utils';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import DateTimeDisplay from '../DateTimeDisplay/DateTimeDisplay';
import { AddToCalendarButton } from '../AddToCalendarSelector/AddToCalendarButton';
import { isGiftCardServiceModule } from '@gik/calendar/constants/services/ServiceModules';
import { useUserStore } from '@gik/core/store/UserStore';

export interface ICalendarClaimHeaderProps extends React.HTMLAttributes<HTMLElement> {
  event: ICalendarEvent;
  entry: ICalendarEntry;
  eventType: IWordpressCalendarEventType;
  serviceSlug?: string;
  serviceCategorySlug?: string;
  showServiceType?: boolean;
  showPetCareType?: boolean;
}

export default function CalendarClaimHeader({
  entry,
  event,
  eventType,
  className,
  serviceSlug,
  serviceCategorySlug,
  showServiceType,
  showPetCareType,
  ...otherProps
}: ICalendarClaimHeaderProps): React.ReactElement {
  const bem = bemBlock('calendar-claim-header');

  const { data: calendarEventTypes } = useCalendarEventTypes();
  const petCareEventType = calendarEventTypes?.find(item => item.id === CalendarEventTypeIds.PetCare);
  const petCareType =
    eventType === petCareEventType &&
    petCareEventType?.acf.pet_care_types.find(item => item.id === entry.petCareTypeId?.toString());

  const userId = useUserStore(state => state.id);

  const isGiftCardClaim = entry.claim && isGiftCardServiceModule(entry.claim.serviceModule);
  const isClaimer = entry.claim?.supporter?.id === userId;

  function getAllowedServiceTitle() {
    switch (serviceCategorySlug) {
      case CalendarServiceCategorySlugs.ProvideMyself:
        if (isFoodType(eventType.id) || eventType.id === CalendarEventTypeIds.Groceries) {
          return 'Drop-Off Only';
        } else {
          if (serviceSlug === 'phone-call-adult-senior-care') {
            return 'Phone Calls Only';
          } else {
            return 'In-Person Only';
          }
        }

      case CalendarServiceCategorySlugs.LocalRestaurant:
        return 'Meal Delivery Only';
      case CalendarServiceCategorySlugs.GiftCard:
        return 'Gift Cards Only';
      default:
        return 'Unknown Service Type';
    }
  }

  const iconClasses = bem('icon', [
    {
      '2-lines': showServiceType || (showPetCareType && petCareType),
    },
  ]);

  return (
    <header className={bem(null, null, className)} {...otherProps}>
      <section className={bem('label-content')}>
        <div className={iconClasses}>
          {entry.typeId === CalendarEventTypeIds.Other ? (
            <Image inline src={eventType?.acf.taxonomy_svg_icon_alt} />
          ) : (
            <Image inline src={eventType?.acf.taxonomy_svg_icon} />
          )}
        </div>

        <div className={bem('label')}>
          <span className={bem('title')}>
            {entry.typeId === CalendarEventTypeIds.Other ? entry.title : eventType.name}
          </span>

          {entry.numberRequired && <span className={bem('number-of-people')}> for {entry.numberRequired}</span>}
          {showServiceType && <div className={bem('allowedService')}>{getAllowedServiceTitle()}</div>}
          {showPetCareType && petCareType && <div className={bem('allowedService')}>{petCareType.name}</div>}
        </div>
      </section>
      <section className={bem('date-content')}>
        {isClaimer && !isGiftCardClaim && entry.claim && (
          <AddToCalendarButton text="Add" tooltip="Add to calendar" links={entry.claim?.addToCalendarLinks} />
        )}

        <DateTimeDisplay
          dateTimeSeparator={null}
          shortTimes
          startsAt={event.startsAt}
          endsAt={event.endsAt}
          allDay={entry.allDay}
        />
      </section>
    </header>
  );
}
